import _get from 'lodash/get';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Page from '../../lib/page';
import { SELECT_RESULT, CONTACT_OPTION, CALL_US_PHONE_INFO } from '../constants/PropTypesShapes';
import ChatBotAssistant from '../components/ChatBotAssistant';
import { loadModalContainer } from '../actions/action-login-modal';
import { chatBotClickedAction } from '../actions/action-chat-bot';
import { trackPage } from '../../lib/analytics-helper';
import { chatBotMessageUsRefIdsMapping } from '../../common/commonConstants';
import {
  getModalChannelType,
  filterC3MarketsConfig,
  returnChannelsFromC3MerchantRC,
  getC3MarketChannelEligibility,
  isC2MerchantMigrated,
  validateAMFlag,
} from '../../lib/globalHelper';
import { requestLoggedOutChatValidation } from '../../lib/loggedOutChatHelper';
import { IDIChannelMapping, isAsyncUser } from '../../lib/asyncMessageHelper';

const isLoggedIn = Page.isLoggedIn();

const ChatBotContainer = function (props) {
  const {
    showChatBot,
    layer,
    chatBotLoading,
    smcResultKeeper,
    display,
    selectResult,
    chatBotAsync,
    contactInfo,
    callusLoading,
    chatPillOcpParams,
  } = props;
  let { contactChannels } = props;

  // Async User Validation
  const isChatBotAsyncUser = isAsyncUser(chatBotAsync);

  // Remove chatbot option when country is enabled and channel is not present in c3merchantMigration remoteconfig or
  // if user is isC2MerchantMigrated
  const displayChatBot = showChatBot && getC3MarketChannelEligibility(IDIChannelMapping.CHAT_BOT);

  // Filter contact channels FPTI based on c3merchantMigration RC Channels when country is enabled in it or
  // if user is isC2MerchantMigrated
  if (filterC3MarketsConfig()) {
    // if market is enabled in c3merchantMigration RC, check for channels in the same remote config
    contactChannels = contactChannels.filter(
      (channel) => returnChannelsFromC3MerchantRC().indexOf(channel.type) !== -1,
    );
  }

  const amPhoneNumber = contactInfo && contactInfo.Account_Manager_Phone_Number;
  const amEmail = contactInfo && contactInfo.Account_Manager_Email_Address;

  // filtering callus if market is present in covidnonidi market
  // Short term update to remove callUs from contact options for BCP COVID-19 non idi enabled markets.
  // This is based in the bcp non idi enabled markets in remote config.
  // and callus response should not contain am flag
  if (Page.getIsBcpNonIdiEnabledMarket() && !isC2MerchantMigrated() && !amPhoneNumber && !amEmail) {
    // exception for C2 Migrated merchant
    contactChannels = contactChannels.filter((channel) => channel.type !== 'callus');
  }

  // adding props.smcResultKeeper.topicTreeSelectResult condition for logout to login
  // from final-intent as duplicate FPTI's are firing for default contact options
  // only after callus response is loaded if the API is called for AM flag for non-idi markets
  // Check for callusloading for non idi markets as we are calling callus service for non idi markets
  if (
    contactChannels &&
    !chatBotLoading &&
    layer <= 1 &&
    display &&
    !smcResultKeeper.topicTreeSelectResult &&
    ((validateAMFlag() && !callusLoading) || !validateAMFlag())
  ) {
    const parsedChannels = Page.joinContactOptions(contactChannels);
    if (displayChatBot) {
      trackPage({ id: 'contactUsPageContactOptions', link: `smarthelp|default|${parsedChannels}|chatBot` });
    } else {
      trackPage({ id: 'contactUsPageContactOptions', link: `smarthelp|default|${parsedChannels}` });
    }
  }

  let selectResultRefId = chatBotMessageUsRefIdsMapping.GENPOOL_CONTACT_US; // GENPOOL is generic topic id
  const topicRefId = _get(selectResult, 'topicTreeSelectResult.topicRefId');
  const topicName = _get(selectResult, 'topicTreeSelectResult.topicName');
  let link = 'smarthelp-chat-bot-contact-us';

  // Function passed to the child in this case ChatBot.js to control the login form flow.
  function chatBotClicked() {
    // Logged Out Logic
    // Let's open up the login modal.
    if (!isLoggedIn) {
      props.chatBotClickedAction();

      // Check if the logged out chat is enabled for the selected final intent topic.
      if (topicRefId) {
        props.requestLoggedOutChatValidation(topicRefId);
      }
      // If the customer (IDI controlled ) is Async / Pilot User or async user from chatbot, display "Message Us " than "chat with us"
      return props.loadModalContainer(getModalChannelType(isChatBotAsyncUser));
    }

    return null;
  }

  if (topicRefId) {
    selectResultRefId = `${topicRefId}|finalIntent`;
    link = `${link}|final|${selectResultRefId}`;
  }
  let chatPillOcpParamsValues = null;
  if (!selectResult.topicsDisplay) {
    chatPillOcpParamsValues = chatPillOcpParams;
  }

  return (
    <ChatBotAssistant
      link={link}
      id="chatBot"
      display={displayChatBot}
      topicRefId={selectResultRefId}
      subject={topicName}
      chatPillOcpParams={chatPillOcpParamsValues}
      chatBotClicked={chatBotClicked}
      isChatBotAsyncUser={isChatBotAsyncUser}
    />
  );
};

function mapStateToProps(state) {
  return {
    selectResult: state.selectResult,
    smcResultKeeper: state.smcResultKeeper,
    layer: state.topicSelect.layer,
    showChatBot: state.postChatBotEligibility.chatBotDisplay,
    contactChannels: state.contactChannels.channels,
    chatBotLoading: state.postChatBotEligibility.isLoading,
    display: state.contactChannels.display,
    chatBotAsync: state.postChatBotEligibility.chatBotAsync,
    callusLoading: state.callUs.isLoading,
    contactInfo: state.callUs.callUsResult && state.callUs.callUsResult.contactInfo,
    chatPillOcpParams: state.ocpContextResponse.setContextIntents,
  };
}

ChatBotContainer.propTypes = {
  selectResult: PropTypes.shape(SELECT_RESULT).isRequired,
  smcResultKeeper: PropTypes.shape(SELECT_RESULT).isRequired,
  layer: PropTypes.number.isRequired,
  chatBotClickedAction: PropTypes.func,
  showChatBot: PropTypes.bool.isRequired,
  chatBotLoading: PropTypes.bool.isRequired,
  display: PropTypes.bool.isRequired,
  contactChannels: PropTypes.arrayOf(PropTypes.shape(CONTACT_OPTION)),
  chatBotAsync: PropTypes.bool.isRequired,
  requestLoggedOutChatValidation: PropTypes.func.isRequired,
  loadModalContainer: PropTypes.func.isRequired,
  callusLoading: PropTypes.bool.isRequired,
  contactInfo: PropTypes.shape(CALL_US_PHONE_INFO).isRequired,
  chatPillOcpParams: PropTypes.string,
};

ChatBotContainer.defaultProps = {
  chatBotClickedAction: () => {},
  contactChannels: [],
};

function mapDispatchToProps(dispatch) {
  return bindActionCreators({ chatBotClickedAction, requestLoggedOutChatValidation, loadModalContainer }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(ChatBotContainer);
